/**
 * Saving a table as a file: the formats, the file name, the grouping of rows and the download.
 * The cells are the raw values the table's export columns give (see `CsvColumn`).
 */
import { csvText, type CsvValue } from './csv';
import { buildPdf } from './pdf';
import { sortRows } from './useTableSort';
import { buildXlsx } from './xlsx';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface ExportFormatInfo {
  id: ExportFormat;
  label: string;
  extension: string;
  type: string;
  /** One line for the dialog: what the file is good for. */
  hint: string;
}

const CSV_FORMAT: ExportFormatInfo = {
  id: 'csv',
  label: 'CSV',
  extension: 'csv',
  type: 'text/csv;charset=utf-8',
  hint: 'Comma-separated text (UTF-8): opens in any spreadsheet, script or BI tool.',
};

export const EXPORT_FORMATS: readonly ExportFormatInfo[] = [
  CSV_FORMAT,
  {
    id: 'xlsx',
    label: 'Excel',
    extension: 'xlsx',
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    hint: 'Workbook with typed numbers and dates, a frozen header row and column filters.',
  },
  {
    id: 'pdf',
    label: 'PDF',
    extension: 'pdf',
    type: 'application/pdf',
    hint: 'Printable table on A4 pages, the header row repeated on every page.',
  },
];

export const exportFormat = (id: ExportFormat): ExportFormatInfo =>
  EXPORT_FORMATS.find((format) => format.id === id) ?? CSV_FORMAT;

/** A run of rows with the same value in the group column. */
export interface ExportGroup {
  start: number;
  count: number;
  label: string;
}

export interface ExportTable {
  /** The table's name: the file name, the sheet name, the PDF title. */
  name: string;
  /** For the PDF: what the rows are, when they were exported. */
  subtitle?: string;
  headers: readonly string[];
  rows: readonly (readonly CsvValue[])[];
  groups?: readonly ExportGroup[];
}

/** Text of a cell as a group label. */
const groupText = (value: CsvValue) =>
  value === null || value === undefined || value === ''
    ? '(empty)'
    : typeof value === 'boolean'
      ? value
        ? 'Yes'
        : 'No'
      : String(value);

/**
 * The rows ordered by one column (empty values last, as the tables sort), with the runs of equal
 * values as groups labelled "<header>: <value>".
 */
export function groupRows(
  rows: readonly (readonly CsvValue[])[],
  column: number,
  header: string,
): { rows: (readonly CsvValue[])[]; groups: ExportGroup[] } {
  const sorted = sortRows(rows, (row) => row[column], 'asc');
  const groups: ExportGroup[] = [];
  sorted.forEach((row, index) => {
    const label = `${header}: ${groupText(row[column])}`;
    const last = groups.at(-1);
    if (last?.label === label) last.count += 1;
    else groups.push({ start: index, count: 1, label });
  });
  return { rows: sorted, groups };
}

/** "mrr-bridge-2026-10-15.csv" from "MRR bridge", the format and the day of the export. */
export function exportFileName(name: string, format: ExportFormat, date = new Date()): string {
  const slug =
    name
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '') || 'export';
  const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return `${slug}-${day}.${exportFormat(format).extension}`;
}

/** The file's bytes (or text) and media type. */
export function buildExport(
  table: ExportTable,
  format: ExportFormat,
  date = new Date(),
): { content: string | Uint8Array; type: string } {
  const { type } = exportFormat(format);
  switch (format) {
    case 'csv':
      return { content: csvText(table.headers, table.rows), type };
    case 'xlsx':
      return { content: buildXlsx({ ...table, name: table.name }, date), type };
    case 'pdf':
      return { content: buildPdf({ ...table, title: table.name }, date), type };
  }
}

/** Saves content as a file through the browser's download (a temporary object URL on a link). */
export function downloadFile(fileName: string, content: string | Uint8Array, type: string) {
  const url = URL.createObjectURL(new Blob([content as BlobPart], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
  // Firefox needs the URL a moment after the click.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

/** Builds the file and downloads it; returns its name. */
export function exportTable(table: ExportTable, format: ExportFormat, date = new Date()): string {
  const fileName = exportFileName(table.name, format, date);
  const { content, type } = buildExport(table, format, date);
  downloadFile(fileName, content, type);
  return fileName;
}
